import React, {forwardRef, useCallback, useState} from 'react';
import style from './style.module.scss';
import {Gallery} from 'components/ProductSnippetNew/components/Gallery';
import {Labels} from 'components/ProductSnippetNew/components/Labels';
import {CartActions} from 'components/ProductSnippetNew/components/CartActions';
import {useProductSnippetLayout} from 'components/ProductSnippetNew/hooks/useProductSnippetLayout';
import {ProductSnippetHeader} from 'components/ProductSnippetNew/components/ProductSnippetHeader';
import {ProductSnippetContent} from 'components/ProductSnippetNew/components/ProductSnippetContent';
import cs from 'classnames';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {Wineries} from 'components/ProductSnippetNew/components/Wineries';

type TProductSnippetProps = IProductSnippet;

export const ProductSnippetNew = forwardRef<HTMLDivElement, IProductSnippet>(
    (props: TProductSnippetProps, externalRef) => {
        const {
            className,
            id,
            url,
            title,
            // isArt,
            description,
            images,
            price,
            ratings,
            reviewsCount,
            reviewsUrl,
            isFineAndRare,
            labels,
            availability,
            wishlist,
            cart,
            properties,
            analytics: {onProductClick, onStockClick, onReviewsClick},
            hasBorder,
            stickers,
            hasCartActions,
            isCompact = false,
        } = props;

        const {old, current, badges, currency, unit, percent, cashback} = price;

        const {mainRef, mainInnerRef, pixelOverflow} = useProductSnippetLayout();
        const [isFirstImageActive, setIsFirstImageActive] = useState(true);

        const setRefs = useCallback(
            (element: HTMLDivElement | null) => {
                if (mainRef) {
                    (mainRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
                }

                if ('function' === typeof externalRef) {
                    externalRef(element);
                } else if (externalRef) {
                    externalRef.current = element;
                }
            },
            [mainRef, externalRef],
        );

        const handleActiveImageChange = useCallback((index: number) => {
            setIsFirstImageActive(0 === index);
        }, []);

        return (
            <div
                ref={setRefs}
                className={cs(style.main, className, {
                    [style.main_bordered]: hasBorder,
                })}
            >
                <div ref={mainInnerRef} className={style.main__inner}>
                    <ProductSnippetHeader
                        id={id}
                        reviewsUrl={reviewsUrl}
                        isFineAndRare={isFineAndRare}
                        reviewsCount={reviewsCount}
                        ratings={ratings}
                        // isArt={isArt}
                        onReviewsClick={onReviewsClick}
                        stickers={stickers}
                        wishlist={wishlist}
                        isFirstImageActive={isFirstImageActive}
                    />
                    <Gallery
                        url={url}
                        imageList={images}
                        onProductClick={onProductClick}
                        availability={availability}
                        onActiveImageChange={handleActiveImageChange}
                    />
                    <Wineries wineries={15} availability={availability} />
                    <Labels labels={labels} id={id} onStockClick={onStockClick} />
                    <ProductSnippetContent
                        title={title}
                        description={description}
                        badges={badges}
                        pixelOverflow={pixelOverflow}
                        percent={percent}
                        cashback={cashback}
                        old={old}
                        current={current}
                        currency={currency}
                        unit={unit}
                        onProductClick={onProductClick}
                        properties={properties}
                        url={url}
                        isCompact={isCompact}
                    />
                    {hasCartActions && <CartActions availability={availability} cart={cart} url={url} />}
                </div>
            </div>
        );
    },
);
